import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageMeta from "@/components/feature/PageMeta";
import { titles } from "@/mocks/titles";
import { CONTACT_EMAIL, CONTACT_WECHAT, SITE_NAME } from "@/utils/site";

const stats = [
  { label: "在库影视", value: `${titles.length}`, suffix: "部" },
  { label: "内容分类", value: "2", suffix: "大类" },
  { label: "题材标签", value: "20+", suffix: "个题材" },
  { label: "字幕版本", value: "中文字幕", suffix: "为主" },
];

const usage = [
  {
    icon: "ri-search-line",
    title: "关键词搜索",
    desc: "在顶部搜索框输入片名、导演、演员或剧情关键词，回车即可进入搜索结果页，支持继续筛选与排序。",
  },
  {
    icon: "ri-filter-3-line",
    title: "分类与筛选",
    desc: "进入电影或电视剧频道后，按地区、年份、题材逐层缩小范围；筛选页已默认展开全部条件，一屏内完成挑选。",
  },
  {
    icon: "ri-bookmark-line",
    title: "收藏与分享",
    desc: "在影视详情页点击收藏即可保存到本机浏览器，无需注册登录；分享按钮会复制当前页面链接。",
  },
  {
    icon: "ri-play-circle-line",
    title: "播放演示区",
    desc: "详情页播放区为前端演示占位，点击后会提示演示状态。站内不提供真实视频播放与下载功能。",
  },
];

export default function AboutPage() {
  return (
    <Layout>
      <PageMeta
        title="关于我们 - 中文字幕在线免费看"
        description="了解中文字幕在线免费看网站的定位、内容范围、使用说明、联系方式与免责声明，本站为影视内容演示站点。"
        keywords="关于中文字幕在线免费看,影视网站介绍,免责声明,联系方式"
      />

      <section className="relative overflow-hidden bg-primary-950 px-4 py-12 md:px-8 md:py-16">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-950 to-primary-950"></div>
        <div className="relative max-w-[820px]">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-400/40 bg-accent-500/10 px-3 py-1 text-[11px] text-accent-300">
            <i className="ri-information-line text-sm" aria-hidden="true"></i>
            关于本站
          </span>
          <h1 className="mt-4 font-heading text-2xl font-semibold leading-snug text-background-50 md:text-4xl">
            {SITE_NAME}
            <span className="mt-2 block text-sm font-normal text-background-300 md:text-base">
              把中文字幕影视信息整理清楚，让找片这件事简单一点
            </span>
          </h1>
          <p className="mt-5 max-w-[720px] text-[13px] leading-7 text-background-200 md:text-sm md:leading-8">
            本站是一个以中文字幕电影、电视剧为核心的内容门户，按分类与题材整理影视条目，提供关键词搜索、条件筛选、详情信息与相关推荐。
            我们更关注「找得到、看得明白」，因此在每个详情页标注导演、主演、语言、片长与集数，方便你在点开之前就判断是否合口味。
          </p>
        </div>

        <div className="relative mt-8 grid grid-cols-2 gap-3 md:mt-10 md:grid-cols-4 md:gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg border border-primary-800 bg-primary-900/40 px-4 py-3.5">
              <p className="font-heading text-xl font-semibold text-accent-400 md:text-2xl">{stat.value}</p>
              <p className="mt-1 text-[11px] text-background-400">
                {stat.label} · {stat.suffix}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-12">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div>
            <h2 className="flex items-center gap-2.5 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              使用说明
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {usage.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100">
                    <i className={`${item.icon} text-base text-primary-700`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-3 text-sm font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-6 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-10 flex items-center gap-2.5 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              免责声明与版权说明
            </h2>
            <div className="mt-4 space-y-3 rounded-lg border border-background-200 bg-background-100 px-4 py-4 text-xs leading-6 text-foreground-700 md:px-5 md:py-5">
              <p>
                1. 本站为影视内容演示站点，站内影视名称、简介、评分与海报均为演示数据，用于展示页面的分类、筛选与详情结构，不代表真实作品信息。
              </p>
              <p>
                2. 本站不存储、不制作、不传播任何视频文件，播放区域仅为前端演示占位，不具备真实播放与下载能力。
              </p>
              <p>
                3. 站内不提供任何盗版资源或下载链接。如需观看完整影视作品，请前往正版授权平台，支持正版内容创作。
              </p>
              <p>
                4. 若站内演示素材存在版权疑问，请联系下方邮箱，我们会在核实后第一时间处理。
              </p>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
              <h2 className="text-sm font-semibold text-foreground-950">联系方式</h2>
              <ul className="mt-3 space-y-3 text-xs text-foreground-700">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className="ri-mail-line text-sm text-primary-600" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">合作与内容反馈</span>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="cursor-pointer break-all text-primary-700 hover:underline">
                      {CONTACT_EMAIL}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className="ri-wechat-line text-sm text-primary-600" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">微信公众号</span>
                    {CONTACT_WECHAT}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className="ri-time-line text-sm text-primary-600" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">处理时效</span>
                    工作日 10:00 - 18:00
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-primary-950 px-4 py-5">
              <h2 className="text-sm font-semibold text-background-50">继续浏览</h2>
              <div className="mt-3 flex flex-col gap-2">
                <Link to="/category/movie" className="flex cursor-pointer items-center justify-between rounded border border-primary-800 px-3 py-2 text-xs text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300">
                  电影频道
                  <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                </Link>
                <Link to="/category/tv" className="flex cursor-pointer items-center justify-between rounded border border-primary-800 px-3 py-2 text-xs text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300">
                  电视剧频道
                  <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                </Link>
                <Link to="/category/movie/filter" className="flex cursor-pointer items-center justify-between rounded border border-primary-800 px-3 py-2 text-xs text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300">
                  条件筛选页
                  <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}